/**
 * SprintActivityMath — small card that shows the per-week outreach math
 * required to hit the sprint goal at the tenant's avg deal size.
 *
 * closes_needed   = (sprint_goal - sprint_actual - sprint_committed) / avg_deal_size
 * weeks_remaining = ceil(days_remaining / 7), min 1
 * closes_per_week = closes_needed / weeks_remaining
 * quals_per_week  = closes_per_week / close_rate (default 0.20)
 * meetings_per_week = quals_per_week * 5
 * outreach_per_week = meetings_per_week * 5
 */
import { useSprintConfig } from "@/lib/useSprintConfig";
import { useMyGoals } from "@/lib/useMyGoals";

interface Props {
  avgDealSize?: number;
  closeRate?: number;
}

export function SprintActivityMath({ avgDealSize = 80000, closeRate = 0.20 }: Props) {
  const { window: w } = useSprintConfig();
  const { goals } = useMyGoals();

  const remainingGoal = Math.max(0, goals.sprintGoal - goals.sprintActual - goals.sprintCommitted);
  const closesNeeded = avgDealSize > 0 ? remainingGoal / avgDealSize : 0;
  const weeksRemaining = Math.max(1, w?.weeksRemaining ?? 8);
  const closesPerWeek = closesNeeded / weeksRemaining;
  const qualsPerWeek = closeRate > 0 ? closesPerWeek / closeRate : 0;
  const meetingsPerWeek = qualsPerWeek * 5;
  const outreachPerWeek = meetingsPerWeek * 5;

  if (goals.sprintGoal === 0) return null;

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
        Sprint activity math
      </div>
      <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Closes / week" value={closesPerWeek.toFixed(1)} />
        <Stat label="Quals / week" value={qualsPerWeek.toFixed(1)} />
        <Stat label="Meetings / week" value={Math.ceil(meetingsPerWeek).toString()} />
        <Stat label="Outreach / week" value={Math.ceil(outreachPerWeek).toString()} />
      </div>
      <div className="mt-2 text-[11px] text-neutral-500">
        Avg deal ${(avgDealSize / 1000).toFixed(0)}k, {Math.round(closeRate * 100)}% close rate, {weeksRemaining} weeks left in sprint.
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-lg font-semibold text-neutral-900">{value}</div>
      <div className="text-[11px] text-neutral-500">{label}</div>
    </div>
  );
}

export default SprintActivityMath;
